// Dev Event Inspector -- the human vetting surface for the live event stream.
//
// A minimal floating panel that subscribes to the debug sink and renders every
// tracked event newest-first: name, actor/source + surface, key props, and a
// timestamp. This is how a human confirms the right events fire with the right
// `source` (human vs agent) while driving the app or the agent API.
//
// Rendered only when asked for -- `?debugEvents=1` or `localStorage pmt.debug=1`
// (analytics/debugFlag.ts), in a dev build too: the iPad is driven against the dev
// server, and a pill that is on by default sat on the work surface of every review
// session. Styling is inline + self-contained so it needs no CSS wiring.

import { useEffect, useState } from 'react';
import { subscribeEvents, getEvents, clearEvents, type EnrichedEvent } from '@/analytics';
import { debugFlagOn } from '@/analytics/debugFlag';
import { Button } from '@/components/ui/primitives';
import { ChevronDown, ChevronUp } from '@/components/ui/icons';

function propsSummary(props: Record<string, string | number | boolean>): string {
    const entries = Object.entries(props);
    if (entries.length === 0) return '';
    return entries.map(([k, v]) => `${k}=${v}`).join('  ');
}

export function EventInspector() {
    const [events, setEvents] = useState<readonly EnrichedEvent[]>(() => getEvents());
    const [open, setOpen] = useState(false);
    // Render nothing until mounted so the first client render matches the SSG
    // output (null) -- shouldShow() reads window.location, which the server can't,
    // so gating on it during render would be a hydration mismatch (#418/#423).
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // Seed from the current buffer, then subscribe to live changes.
        setEvents(getEvents());
        return subscribeEvents(next => setEvents(next.slice()));
    }, []);

    if (!mounted || !debugFlagOn()) return null;

    const ordered = events.slice().reverse(); // newest first

    return (
        <div
            data-testid="event-inspector"
            // Bottom-LEFT of the CANVAS (moved off bottom-right, which crowded
            // the review rail / queue panel there): --pmt-canvas-left (ViewerShell)
            // is the canvas's left edge, past the plan chest's pulls and a docked
            // Review / Data drawer; 92 px outside a viewer.
            //
            // `bottom: 56` (not 8): the left nav's "Document data" footer
            // button (ViewerLeftNav.tsx .left-nav-footer) sits in normal flow
            // at the true bottom of that column, which is the SAME screen
            // region as a bottom-left fixed overlay whenever the left nav is
            // expanded (its own width band starts at x=0, well past this
            // pill's left:92) -- 56px clears that footer's height (~50px)
            // with margin, at every tested viewport. Plus --pmt-bottom-inset
            // (ViewerShell): the review bottom sheet's covered height, so
            // in sheet mode this rides above the sheet instead of on it.
            style={{
                position: 'fixed',
                bottom: 'calc(var(--pmt-bottom-inset, 0px) + 56px)',
                left: 'calc(var(--pmt-canvas-left, 80px) + 12px)',
                zIndex: 'var(--z-debug)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11,
                color: 'var(--text-on-scrim)',
                pointerEvents: 'none',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'flex-start', pointerEvents: 'auto' }}>
                <Button size="small" icon={open ? ChevronDown : ChevronUp} aria-expanded={open} onClick={() => setOpen(o => !o)}>
                    events {events.length}
                </Button>
            </div>

            {open && (
                <div
                    data-testid="event-inspector-panel"
                    style={{
                        marginTop: 6,
                        width: 340,
                        maxHeight: 300,
                        overflowY: 'auto',
                        background: 'var(--color-scrim)',
                        border: '1px solid var(--bk-border)',
                        borderRadius: 0,
                        padding: 8,
                        pointerEvents: 'auto',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 6,
                        }}
                    >
                        <strong style={{ color: 'var(--text-on-scrim)' }}>PMT event stream</strong>
                        <Button size="small" variant="ghost" onClick={() => clearEvents()}>
                            clear
                        </Button>
                    </div>

                    {ordered.length === 0 && (
                        <div style={{ color: 'var(--text-on-scrim)' }}>No events yet. Interact with the app.</div>
                    )}

                    {ordered.map((e, i) => {
                        const isAgent = e.source === 'agent';
                        return (
                            <div
                                key={`${e.timestamp}-${i}`}
                                style={{
                                    padding: '4px 0',
                                    borderTop: i === 0 ? 'none' : '1px solid var(--bk-border)',
                                }}
                            >
                                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                    <span
                                        style={{
                                            color: 'var(--text-on-scrim)', textDecoration: isAgent ? 'underline' : 'none',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {e.source}/{e.surface}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{e.event}</span>
                                    <span style={{ marginLeft: 'auto', color: 'var(--text-on-scrim)' }}>
                                        {e.timestamp.slice(11, 19)}
                                    </span>
                                </div>
                                {propsSummary(e.props) && (
                                    <div style={{ color: 'var(--text-on-scrim)', marginTop: 2 }}>
                                        {propsSummary(e.props)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
